import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import InventoryPage from './pages/InventoryPage';
import AddWatchPage from './pages/AddWatchPage';
import EditWatchPage from './pages/EditWatchPage';
import WatchDetailPage from './pages/WatchDetailPage';
import ClientsPage from './pages/ClientsPage';
import AddClientPage from './pages/AddClientPage';
import EditClientPage from './pages/EditClientPage';
import ClientDetailPage from './pages/ClientDetailPage';
import SalesPage from './pages/SalesPage';
import RecordSalePage from './pages/RecordSalePage';
import SaleDetailPage from './pages/SaleDetailPage';
import InvoicesPage from './pages/InvoicesPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import ServicingPage from './pages/ServicingPage';
import AddServicePage from './pages/AddServicePage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import MarketplacePage from './pages/MarketplacePage';
import NetworkPage from './pages/NetworkPage';
import DealerProfilePage from './pages/DealerProfilePage';
import MessagesPage from './pages/MessagesPage';
import WishListPage from './pages/WishListPage';
import ProfilePage from './pages/ProfilePage';
import AdminDashboard from './pages/AdminDashboard';
import PublicWatchPage from './pages/PublicWatchPage';
import LandingPage from './pages/LandingPage';
import AppointmentsPage from './pages/AppointmentsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import PendingApprovalPage from './pages/PendingApprovalPage';
import PublicDealerProfilePage from './pages/PublicDealerProfilePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import UsagePolicyPage from './pages/UsagePolicyPage';
import DataDestructionPolicyPage from './pages/DataDestructionPolicyPage';
import EditServicePage from './pages/EditServicePage';
import AccountingDashboard from './pages/AccountingDashboard';
import GeneralJournalPage from './pages/GeneralJournalPage';
import RecordExpensePage from './pages/RecordExpensePage';
import ManageExpensesPage from './pages/ManageExpensesPage';
import ManageVendorsPage from './pages/ManageVendorsPage';
import ManageAssetsPage from './pages/ManageAssetsPage';
import ChartOfAccountsPage from './pages/ChartOfAccountsPage';
import AddEditAccountPage from './pages/AddEditAccountPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#fafafa]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#e5e5e5] border-t-[#0a0a0a] rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-[#737373]">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/" />;
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#fafafa]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#e5e5e5] border-t-[#0a0a0a] rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-[#737373]">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? <Navigate to="/dashboard" /> : children;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />

          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />

          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            }
          />

          <Route
            path="/verify-email"
            element={<VerifyEmailPage />}
          />

          <Route
            path="/pending-approval"
            element={<PendingApprovalPage />}
          />

          <Route
            path="/watch/:id"
            element={<PublicWatchPage />}
          />

          <Route
            path="/dealer/:dealerId"
            element={<PublicDealerProfilePage />}
          />

          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsConditionsPage />} />
          <Route path="/usage-policy" element={<UsagePolicyPage />} />
          <Route path="/data-destruction-policy" element={<DataDestructionPolicyPage />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/inventory"
            element={
              <ProtectedRoute>
                <Layout>
                  <InventoryPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/inventory/add"
            element={
              <ProtectedRoute>
                <Layout>
                  <AddWatchPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/inventory/edit/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <EditWatchPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/inventory/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <WatchDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <Layout>
                  <ClientsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/clients/add"
            element={
              <ProtectedRoute>
                <Layout>
                  <AddClientPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/clients/edit/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <EditClientPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/clients/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <ClientDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/sales"
            element={
              <ProtectedRoute>
                <Layout>
                  <SalesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/sales/record"
            element={
              <ProtectedRoute>
                <Layout>
                  <RecordSalePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/sales/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <SaleDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <Layout>
                  <InvoicesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <InvoiceDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/servicing"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServicingPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/servicing/add"
            element={
              <ProtectedRoute>
                <Layout>
                  <AddServicePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/servicing/edit/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <EditServicePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/servicing/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/marketplace"
            element={
              <ProtectedRoute>
                <Layout>
                  <MarketplacePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/network"
            element={
              <ProtectedRoute>
                <Layout>
                  <NetworkPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/network/profile/:dealerId"
            element={
              <ProtectedRoute>
                <Layout>
                  <DealerProfilePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <Layout>
                  <MessagesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/wishlists"
            element={
              <ProtectedRoute>
                <Layout>
                  <WishListPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/appointments"
            element={
              <ProtectedRoute>
                <Layout>
                  <AppointmentsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProfilePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Layout>
                  <AdminDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/accounting"
            element={
              <ProtectedRoute>
                <Layout>
                  <AccountingDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/accounting/general-journal"
            element={
              <ProtectedRoute>
                <Layout>
                  <GeneralJournalPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/accounting/record-expense"
            element={
              <ProtectedRoute>
                <Layout>
                  <RecordExpensePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/accounting/expenses"
            element={
              <ProtectedRoute>
                <Layout>
                  <ManageExpensesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/accounting/vendors"
            element={
              <ProtectedRoute>
                <Layout>
                  <ManageVendorsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/accounting/assets"
            element={
              <ProtectedRoute>
                <Layout>
                  <ManageAssetsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/accounting/chart-of-accounts"
            element={
              <ProtectedRoute>
                <Layout>
                  <ChartOfAccountsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/accounting/accounts/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <AddEditAccountPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Landing Page */}
          <Route path="/" element={<LandingPage />} />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;